import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { MailAlias } from '../types/models/MailAlias';
import type { ApiResponseMailAliases } from '../types/api/ApiResponseMailAlias';

interface UseMailAliasesReturn {
  aliases: MailAlias[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useMailAliases = (mailDomainId: number | undefined): UseMailAliasesReturn => {
  const [aliases, setAliases] = useState<MailAlias[]>([]);
  const [loading, setLoading] = useState(mailDomainId !== undefined);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchAliases = useCallback(async () => {
    if (mailDomainId === undefined) {
      setAliases([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/emails/aliases?mail_domain_id=${mailDomainId}`);

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error((body as { message?: string })?.message ?? `HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseMailAliases;
      if (data.status === 'success' && data.data?.aliases) {
        setAliases(data.data.aliases);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load aliases');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching aliases');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, mailDomainId]);

  useEffect(() => {
    if (mailDomainId === undefined) {
      setAliases([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (auth.isAuthenticated) {
      fetchAliases();
    }
  }, [auth.isAuthenticated, mailDomainId, fetchAliases]);

  return { aliases, loading, error, refetch: fetchAliases };
};
